import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getPlayerProfile, getUserProfile } from "../../services/profileService";
import { supabase } from "../../supabase/supabaseClient";
import { addFavorite, removeFavorite } from "../../services/favoritesService";
import { createConversation, listConversations } from "../../services/messagesService";
import { getNotesForPlayer, createNote, updateNote, deleteNote } from "../../services/notesService";
import { getExperiences } from "../../services/experienceService";
import { getEducations } from "../../services/educationService";
import { getHighlights } from "../../services/highlightsService";
import { getPostsByUser } from "../../services/postsService";
import {
    acceptConnectionRequest,
    getConnectionRelationship,
    rejectConnectionRequest,
    sendConnectionRequest
} from "../../services/connectionsService";
import { followUser, isFollowingUser, unfollowUser } from "../../services/followsService";
import HighlightsDisplay from "../HighlightsDisplay";
import "./PublicProfile.css";

function PublicProfile() {
    // Match route param name `/player/:id`
    const { id } = useParams();
    const navigate = useNavigate();
    const [player, setPlayer] = useState(null);
    const [experiences, setExperiences] = useState([]);
    const [educations, setEducations] = useState([]);
    const [highlights, setHighlights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isScout, setIsScout] = useState(false);
    const [isPlayerProfile, setIsPlayerProfile] = useState(false);
    const [authedUser, setAuthedUser] = useState(null);
    const [favorited, setFavorited] = useState(false);
    const [notes, setNotes] = useState([]);
    const [noteText, setNoteText] = useState("");
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteText, setEditingNoteText] = useState("");
    const [connectionInfo, setConnectionInfo] = useState(null);
    const [loadingConnection, setLoadingConnection] = useState(false);
    const [followingUser, setFollowingUser] = useState(false);
    const [loadingFollow, setLoadingFollow] = useState(false);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [userPosts, setUserPosts] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [orgAssociation, setOrgAssociation] = useState(null);

    const stripOrgPostMarker = (body) => {
        if (typeof body !== "string") return "";
        return body.replace(/\n\n\[posted_as_org:[^\]]+\]\s*$/i, "").trimEnd();
    };

    useEffect(() => {
        const loadProfile = async () => {
            setLoading(true);
            setError("");

            let profileData = null;
            let targetIsPlayer = false;

            let { data: playerData } = await getPlayerProfile(id);

            // Backward compatibility: some parts of the app still route using player profile id.
            if (!playerData) {
                const { data: playerByProfileId } = await supabase
                    .from("player_profiles")
                    .select("*")
                    .eq("id", id)
                    .maybeSingle();
                if (playerByProfileId) {
                    playerData = playerByProfileId;
                }
            }

            if (playerData && (playerData.is_public ?? true)) {
                profileData = playerData;
                targetIsPlayer = true;
            } else {
                const { data: userData } = await getUserProfile(id);
                if (userData && (userData.is_public ?? true)) {
                    profileData = {
                        user_id: userData.user_id,
                        firstname: userData.first_name || "",
                        lastname: userData.last_name || "",
                        avatar_url: userData.avatar_url || "",
                        bio: userData.bio || "",
                        city: userData.city || "",
                        role: userData.role || "member",
                        verified: false,
                        positions: [],
                        DateOfBirth: null,
                        height: null,
                        weight: null,
                        footedness: null,
                        soccer_level: null,
                    };
                }
            }

            if (!profileData) {
                setError("Profile not found or is private.");
                setLoading(false);
                return;
            }

            // Check if user is viewing their own profile
            const { data: sessionData } = await supabase.auth.getSession();
            const user = sessionData?.session?.user ?? null;
            if (user && user.id === profileData.user_id) {
                navigate("/profile");
                return;
            }

            setPlayer(profileData);
            setIsPlayerProfile(targetIsPlayer);

            // Determine current user and scout role
            // Fetch canonical user_profiles row for authenticated user (prefer server-side role)
            let userProfile = null;
            if (user) {
                try {
                    const { data: upData, error: upErr } = await supabase
                        .from("user_profiles")
                        .select("role, user_id, first_name, last_name, city")
                        .eq("user_id", user.id)
                        .maybeSingle();
                    if (!upErr && upData) userProfile = upData;
                } catch (err) {
                    console.error("Error fetching user_profiles for authed user:", err);
                }
            }

            // Attach profile to auth user object for downstream usage
            const authed = user ? { ...user, profile: userProfile } : null;
            setAuthedUser(authed);

            // Fetch connection status if authenticated
            if (authed) {
                try {
                    const relationship = await getConnectionRelationship(profileData.user_id);
                    setConnectionInfo(relationship);
                } catch (err) {
                    console.error("Error fetching connection relationship:", err);
                }

                try {
                    const isFollowing = await isFollowingUser(profileData.user_id);
                    setFollowingUser(!!isFollowing);
                } catch (err) {
                    console.error("Error fetching follow relationship:", err);
                    setFollowingUser(false);
                }
            }

            // Prefer role from `user_profiles.role` (stored lowercase), fall back to auth metadata
            const role = (userProfile && userProfile.role) || user?.user_metadata?.role;
            const roleLower = role && role.toString().toLowerCase();
            const isScoutOrCoach = roleLower === "scout" || roleLower === "coach";
            setIsScout(isScoutOrCoach);

            if (isScoutOrCoach) {
                // Check if already favorited
                if (targetIsPlayer) {
                    const { data: favCheck } = await supabase
                        .from("favorites")
                        .select("id")
                        .eq("scout_id", user.id)
                        .eq("player_id", id)
                        .maybeSingle();
                    setFavorited(!!favCheck);

                    const { data: noteData } = await getNotesForPlayer(user.id, id);
                    setNotes(noteData || []);
                } else {
                    setFavorited(false);
                    setNotes([]);
                }
            }

            // Experiences & education are stored by user_id, not profile id
            const targetUserId = profileData.user_id;

            // Load organization association for this public profile when available.
            try {
                const { data: membership } = await supabase
                    .from("org_memberships")
                    .select("org_id, orgrole")
                    .eq("user_id", targetUserId)
                    .limit(1)
                    .maybeSingle();

                if (membership?.org_id) {
                    const { data: org } = await supabase
                        .from("org_profiles")
                        .select("id, name, type, city")
                        .eq("id", membership.org_id)
                        .maybeSingle();

                    setOrgAssociation({
                        id: membership.org_id,
                        role: membership.orgrole || "",
                        name: org?.name || "",
                        type: org?.type || "",
                        city: org?.city || "",
                    });
                } else {
                    setOrgAssociation(null);
                }
            } catch (orgError) {
                console.error("Error loading organization association:", orgError);
                setOrgAssociation(null);
            }

            if (targetIsPlayer) {
                const { data: expData } = await getExperiences(targetUserId);
                if (expData) setExperiences(expData);

                const { data: eduData } = await getEducations(targetUserId);
                if (eduData) setEducations(eduData);

                const { data: hlData } = await getHighlights(targetUserId);
                if (hlData) setHighlights(hlData);
            } else {
                setExperiences([]);
                setEducations([]);
                setHighlights([]);
            }

            // Load user posts for Activity section
            setPostsLoading(true);
            const { data: postsData, error: postsError } = await getPostsByUser(targetUserId);
            if (!postsError && postsData) {
                const mapped = (postsData || []).map((post) => {
                    const media = Array.isArray(post.post_media)
                        ? [...post.post_media]
                            .sort((a, b) => (a.position || 0) - (b.position || 0))
                            .map((item) => ({ type: item.media_type, url: item.url }))
                        : [];
                    return {
                        id: post.id,
                        body: stripOrgPostMarker(post.body || ""),
                        media,
                        created_at: post.created_at
                    };
                });
                setUserPosts(mapped);
            }
            setPostsLoading(false);

            setLoading(false);
        };

        loadProfile();
    }, [id, navigate]);

    if (loading) {
        return (
            <main className="page">
                <div className="public-profile-container">
                    <section className="profile-hero profile-skeleton">
                        <div className="hero-content">
                            <div className="skeleton-avatar" />
                            <div className="skeleton-hero-text">
                                <div className="skeleton-line skeleton-line--name" />
                                <div className="skeleton-line skeleton-line--subtitle" />
                                <div className="skeleton-line skeleton-line--meta" />
                            </div>
                        </div>
                    </section>
                    <div className="profile-sections">
                        {[1, 2, 3].map((index) => (
                            <section key={index} className="profile-card profile-skeleton-card">
                                <div className="skeleton-line skeleton-line--title" />
                                <div className="skeleton-line skeleton-line--full" />
                                <div className="skeleton-line skeleton-line--full" />
                                <div className="skeleton-line skeleton-line--half" />
                            </section>
                        ))}
                    </div>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className="page">
                <div className="public-profile-container">
                    <div className="error-section">
                        <p>{error}</p>
                        <Link to="/explore" className="btn-secondary">
                            ← Back to Explore
                        </Link>
                    </div>
                </div>
            </main>
        );
    }

    if (!player) {
        return (
            <main className="page">
                <div className="public-profile-container">
                    <p>Profile not found.</p>
                </div>
            </main>
        );
    }

    const getAge = (dob) => {
        if (!dob) return null;
        const birth = new Date(dob);
        if (Number.isNaN(birth.getTime())) return null;
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const age = isPlayerProfile ? getAge(player.DateOfBirth) : null;
    const fullName = `${player.firstname || ""} ${player.lastname || ""}`.trim();
    const initial = fullName.charAt(0).toUpperCase() || "P";
    const roleLabel = player.role
        ? player.role.toString().replace(/\b\w/g, (c) => c.toUpperCase())
        : "Member";
    const targetRole = (player.role || "").toString().toLowerCase();
    const targetIsCompany = targetRole === "company" || targetRole === "organization" || targetRole === "org";
    const connectionStatus = connectionInfo?.status || null;
    const isRequester = connectionInfo?.requester_id === authedUser?.id;
    const isAddressee = connectionInfo?.addressee_id === authedUser?.id;
    const canConnect = !targetIsCompany;

    const handleMessageClick = async () => {
        const { data: existingConversations } = await listConversations(authedUser.id);
        const existing = existingConversations?.find(
            (conv) => (conv.initiator_id === player.user_id && conv.recipient_id === authedUser.id) ||
                (conv.initiator_id === authedUser.id && conv.recipient_id === player.user_id)
        );

        if (existing) {
            navigate("/messages", { state: { conversationId: existing.id } });
            return;
        }

        const { data, error } = await createConversation(authedUser.id, player.user_id);
        if (error) {
            console.error("Failed to create conversation:", error);
            setError("Could not start conversation. Please try again later.");
            return;
        }

        if (data) {
            navigate("/messages", { state: { conversationId: data.id } });
        }
    };

    const handleConnectPrimary = async () => {
        if (!canConnect || loadingConnection) return;
        if (connectionStatus === "accepted" || (connectionStatus === "pending" && isRequester)) return;

        setLoadingConnection(true);
        try {
            if (connectionStatus === "pending" && isAddressee) {
                await acceptConnectionRequest(connectionInfo.id);
                setConnectionInfo({ ...connectionInfo, status: "accepted" });
                setFollowingUser(true);
            } else {
                await sendConnectionRequest(player.user_id);
                setConnectionInfo({
                    id: null,
                    requester_id: authedUser.id,
                    addressee_id: player.user_id,
                    status: "pending"
                });
            }
        } catch (err) {
            console.error("Error handling connect action:", err);
            setError("Could not update connection request. Please try again.");
        } finally {
            setLoadingConnection(false);
        }
    };

    const handleDeclineRequest = async () => {
        if (!(connectionStatus === "pending" && isAddressee) || !connectionInfo?.id) return;
        setLoadingConnection(true);
        try {
            await rejectConnectionRequest(connectionInfo.id);
            setConnectionInfo({ ...connectionInfo, status: "rejected" });
            setMoreMenuOpen(false);
        } catch (err) {
            console.error("Error declining connection request:", err);
            setError("Could not decline request. Please try again.");
        } finally {
            setLoadingConnection(false);
        }
    };

    const handleToggleFollow = async () => {
        if (connectionStatus === "accepted") return;
        setLoadingFollow(true);
        try {
            if (followingUser) {
                await unfollowUser(player.user_id);
                setFollowingUser(false);
            } else {
                await followUser(player.user_id);
                setFollowingUser(true);
            }
        } catch (err) {
            console.error("Error toggling follow status:", err);
            setError("Could not update follow status. Please try again.");
        } finally {
            setLoadingFollow(false);
        }
    };

    const handleToggleFavorite = async () => {
        if (!isScout || !isPlayerProfile) return;
        try {
            if (favorited) {
                await removeFavorite(authedUser.id, id);
                setFavorited(false);
            } else {
                const res = await addFavorite(authedUser.id, id);
                if (res.error) {
                    console.error("PublicProfile: addFavorite error", res.error);
                    setError("Could not add favorite. Check console for details.");
                    return;
                }
                setFavorited(true);
            }
            setMoreMenuOpen(false);
        } catch (e) {
            console.error("PublicProfile: add/remove favorite threw", e);
            setError("Could not update favorite. Check console for details.");
        }
    };

    return (
        <main className="page">
            <div className="public-profile-container">
                {/* <Link to="/explore" className="back-link">
                    {"\u2190"} Back to Explore
                </Link> */}

                <section className="profile-hero">
                    <div className="hero-content">
                        {player.avatar_url
                            ? <img src={player.avatar_url} alt={fullName} className="avatar-circle avatar-img" />
                            : <div className="avatar-circle">{initial}</div>
                        }
                        <div className="hero-text">
                            <h1 className="profile-name">
                                {fullName}
                                {player.verified && <span className="verified-badge">{"\u2713"}</span>}
                            </h1>
                            <p className="profile-subtitle">{isPlayerProfile ? "Pre-Alpha Dribbl Player Profile" : `${roleLabel} Profile`}</p>
                            <div className="hero-meta">
                                {Number.isFinite(age) && <span className="meta-pill">Age: {age}</span>}
                                {player.city && <span className="meta-pill">City: {player.city}</span>}
                                {orgAssociation?.name && <Link to={`/org/${orgAssociation.id}`} className="meta-pill meta-pill-link">Org: {orgAssociation.name}</Link>}
                                {isPlayerProfile ? (
                                    <div className="meta-tags">
                                        {player.positions && player.positions.length ? (
                                            player.positions.map((pos) => (
                                                <span key={pos} className="meta-tag">
                                                    {pos}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="muted">No positions listed</span>
                                        )}
                                    </div>
                                ) : (
                                    <span className="meta-pill">{roleLabel}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    {authedUser && authedUser.id !== player.user_id && (
                        <div className="hero-actions-row">
                            <div className="hero-actions hero-actions-bottom">
                                {canConnect && (
                                    <button
                                        className={`btn-connect ${
                                            connectionStatus === "accepted" ? "btn-connected" :
                                            connectionStatus === "pending" && isRequester ? "btn-pending" :
                                            connectionStatus === "rejected" ? "btn-rejected" :
                                            "btn-connect-default"
                                        }`}
                                        onClick={handleConnectPrimary}
                                        disabled={loadingConnection || connectionStatus === "accepted" || (connectionStatus === "pending" && isRequester)}
                                    >
                                        {loadingConnection
                                            ? (connectionStatus === "pending" && isAddressee ? "Accepting..." : "Sending...")
                                            : connectionStatus === "accepted"
                                                ? "Connected"
                                                : connectionStatus === "pending" && isRequester
                                                    ? "Pending"
                                                    : connectionStatus === "pending" && isAddressee
                                                        ? "Accept"
                                                        : "Connect"}
                                    </button>
                                )}

                                <button
                                    className="btn-secondary"
                                    onClick={handleMessageClick}
                                >
                                    Message
                                </button>

                                <div className="hero-more-menu-wrap">
                                    <button
                                        className="btn-more"
                                        type="button"
                                        onClick={() => setMoreMenuOpen((prev) => !prev)}
                                        aria-expanded={moreMenuOpen}
                                    >
                                        More
                                    </button>
                                    {moreMenuOpen && (
                                        <div className="hero-more-menu-dropdown">
                                            <button
                                                className="hero-more-item"
                                                type="button"
                                                onClick={handleToggleFollow}
                                                disabled={loadingFollow || connectionStatus === "accepted"}
                                            >
                                                {loadingFollow
                                                    ? "Working..."
                                                    : connectionStatus === "accepted"
                                                        ? "Following (connection)"
                                                        : followingUser
                                                            ? "Unfollow"
                                                            : "Follow"}
                                            </button>

                                            {isScout && isPlayerProfile && (
                                                <button
                                                    className="hero-more-item"
                                                    type="button"
                                                    onClick={handleToggleFavorite}
                                                >
                                                    {favorited ? "Remove Favorite" : "Add to Favorites"}
                                                </button>
                                            )}

                                            {canConnect && connectionStatus === "pending" && isAddressee && (
                                                <button
                                                    className="hero-more-item hero-more-item-danger"
                                                    type="button"
                                                    onClick={handleDeclineRequest}
                                                    disabled={loadingConnection}
                                                >
                                                    {loadingConnection ? "Declining..." : "Decline request"}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                <div className="profile-sections">
                    {player.bio && (
                        <section className="profile-card">
                            <h2>About</h2>
                            <p className="about-text">{player.bio}</p>
                        </section>
                    )}

                    {/* {!isPlayerProfile && orgAssociation?.name && (
                        <section className="profile-card">
                            <h2>Organization</h2>
                            <p className="about-text">
                                {orgAssociation.name}
                                {orgAssociation.type ? ` • ${orgAssociation.type}` : ""}
                                {orgAssociation.city ? ` • ${orgAssociation.city}` : ""}
                            </p>
                        </section>
                    )} */}

                        {isPlayerProfile && highlights.length > 0 && (
                            <section className="profile-card">
                                <HighlightsDisplay highlights={highlights} />
                            </section>
                        )}

                    {isPlayerProfile && (player.height || player.weight || player.city || player.footedness || player.soccer_level) && (
                        <section className="profile-card">
                            <h2>Attributes</h2>
                            <div className="attributes-grid">
                                {player.height && (
                                    <div className="attribute-item">
                                        <span className="attr-label">Height</span>
                                        <span className="attr-value">{player.height} cm</span>
                                    </div>
                                )}
                                {player.weight && (
                                    <div className="attribute-item">
                                        <span className="attr-label">Weight</span>
                                        <span className="attr-value">{player.weight} kg</span>
                                    </div>
                                )}
                                {player.city && (
                                    <div className="attribute-item">
                                        <span className="attr-label">City</span>
                                        <span className="attr-value">{player.city}</span>
                                    </div>
                                )}
                                {player.footedness && (
                                    <div className="attribute-item">
                                        <span className="attr-label">Dominant Foot</span>
                                        <span className="attr-value">{player.footedness}</span>
                                    </div>
                                )}
                                {player.soccer_level && (
                                    <div className="attribute-item">
                                        <span className="attr-label">Soccer Level</span>
                                        <span className="attr-value">{player.soccer_level}</span>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {isPlayerProfile && experiences.length > 0 && (
                        <section className="profile-card">
                            <h2>Experience</h2>
                            <div className="timeline">
                                {experiences.map((exp, index) => (
                                    <div key={exp.id} className="timeline-item">
                                        <div className="timeline-dot"></div>
                                        {index < experiences.length - 1 && <div className="timeline-line"></div>}
                                        <div className="timeline-content">
                                            <h3 className="timeline-title">{exp.team_name}</h3>
                                            <p className="timeline-date">
                                                {new Date(exp.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                                {' - '}
                                                {exp.end_date ? new Date(exp.end_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}
                                            </p>
                                            {exp.position && <p className="timeline-position">{exp.position}</p>}
                                            {exp.description && <p className="timeline-description">{exp.description}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {isPlayerProfile && educations.length > 0 && (
                        <section className="profile-card">
                            <h2>Education</h2>
                            <div className="timeline">
                                {educations.map((edu, index) => (
                                    <div key={edu.id} className="timeline-item">
                                        <div className="timeline-dot"></div>
                                        {index < educations.length - 1 && <div className="timeline-line"></div>}
                                        <div className="timeline-content">
                                            <h3 className="timeline-title">{edu.school_name}</h3>
                                            {(edu.degree || edu.field_of_study) && (
                                                <p className="timeline-position">
                                                    {edu.degree && edu.field_of_study && `${edu.degree} in ${edu.field_of_study}`}
                                                    {edu.degree && !edu.field_of_study && edu.degree}
                                                    {!edu.degree && edu.field_of_study && edu.field_of_study}
                                                </p>
                                            )}
                                            <p className="timeline-date">
                                                {new Date(edu.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                                {' - '}
                                                {edu.end_date ? new Date(edu.end_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}
                                            </p>
                                            {edu.description && <p className="timeline-description">{edu.description}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {isPlayerProfile && player.education && !educations.length && (
                        <section className="profile-card profile-card-horizontal">
                            <h2>Education</h2>
                            <div className="card-horizontal-body">
                                <div className="card-content">
                                    <p>{player.education}</p>
                                </div>
                            </div>
                        </section>
                    )}
                    <section className="profile-card profile-activity-section">
                        <h2>Activity</h2>
                        {postsLoading ? (
                            <div className="activity-showcase">
                                <div className="activity-scroll">
                                    {[1, 2, 3].map((index) => (
                                        <div key={index} className="activity-post-card activity-skeleton-card">
                                            <div className="activity-skeleton-media" />
                                            <div className="activity-post-body">
                                                <div className="activity-skeleton-line activity-skeleton-line--full" />
                                                <div className="activity-skeleton-line activity-skeleton-line--half" />
                                                <div className="activity-skeleton-line activity-skeleton-line--date" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : userPosts.length === 0 ? (
                            <div className="activity-empty">
                                <p>No posts yet.</p>
                            </div>
                        ) : (
                            <div className="activity-showcase">
                                <div className="activity-scroll">
                                    {userPosts.map((post) => (
                                        <div
                                            key={post.id}
                                            className="activity-post-card"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => navigate("/home", {
                                                state: {
                                                    focusPostId: post.id,
                                                    focusPostPreview: {
                                                        id: post.id,
                                                        author: fullName || "User",
                                                        authorId: player.user_id,
                                                        authorAvatarUrl: player.avatar_url || "",
                                                        body: post.body,
                                                        media: post.media,
                                                        created_at: post.created_at,
                                                    }
                                                }
                                            })}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    navigate("/home", {
                                                        state: {
                                                            focusPostId: post.id,
                                                            focusPostPreview: {
                                                                id: post.id,
                                                                author: fullName || "User",
                                                                authorId: player.user_id,
                                                                authorAvatarUrl: player.avatar_url || "",
                                                                body: post.body,
                                                                media: post.media,
                                                                created_at: post.created_at,
                                                            }
                                                        }
                                                    });
                                                }
                                            }}
                                        >
                                            {post.media.length > 0 && (
                                                <div className="activity-post-media">
                                                    {post.media[0].type === "video" ? (
                                                        <video src={post.media[0].url} controls preload="metadata" />
                                                    ) : (
                                                        <img src={post.media[0].url} alt="Post media" />
                                                    )}
                                                </div>
                                            )}
                                            <div className="activity-post-body">
                                                <p>{post.body.length > 120 ? `${post.body.slice(0, 120)}...` : post.body}</p>
                                                <div className="activity-post-date">
                                                    {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                    {isScout && isPlayerProfile && (
                        <section className="profile-card">
                            <h2>Notes</h2>
                            <form
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    if (!authedUser || !noteText.trim()) return;
                                    const { data } = await createNote(authedUser.id, id, noteText.trim());
                                    if (data) {
                                        setNotes([data, ...notes]);
                                        setNoteText("");
                                    }
                                }}
                            >
                                <textarea
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    placeholder="Private notes visible only to you"
                                    rows={3}
                                />
                                <div className="form-actions">
                                    <button className="btn-scout" type="submit">Save Note</button>
                                </div>
                            </form>
                            {notes.length > 0 && (
                                <div className="notes-list">
                                    {notes.map((n) => (
                                        <div key={n.id} className="note-item">
                                            {editingNoteId === n.id ? (
                                                <div>
                                                    <textarea
                                                        value={editingNoteText}
                                                        onChange={(e) => setEditingNoteText(e.target.value)}
                                                        rows={2}
                                                    />
                                                    <div className="note-actions">
                                                        <button
                                                            className="btn-scout"
                                                            onClick={async () => {
                                                                const { data } = await updateNote(n.id, editingNoteText.trim());
                                                                if (data) {
                                                                    setNotes(notes.map((x) => (x.id === n.id ? data : x)));
                                                                    setEditingNoteId(null);
                                                                }
                                                            }}
                                                        >Save</button>
                                                        <button
                                                            className="btn-secondary"
                                                            onClick={() => setEditingNoteId(null)}
                                                        >Cancel</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <p className="note-text">{n.note}</p>
                                                    <p className="note-date">{new Date(n.updated_at).toLocaleDateString()} {new Date(n.updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                                                    <div className="note-actions">
                                                        <button
                                                            className="btn-secondary"
                                                            onClick={() => {
                                                                setEditingNoteId(n.id);
                                                                setEditingNoteText(n.note);
                                                            }}
                                                        >Edit</button>
                                                        <button
                                                            className="btn-secondary"
                                                            onClick={async () => {
                                                                await deleteNote(n.id);
                                                                setNotes(notes.filter((x) => x.id !== n.id));
                                                            }}
                                                        >Delete</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </div>
        </main>
    );
}

export default PublicProfile;
